import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBag, Heart, ChevronLeft, Truck, Shield, ShieldCheck, Sparkles, BellRing, X } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import MoonAvatar from "@/components/MoonAvatar";
import StarLoader from "@/components/StarLoader";
import { AccordionItem } from "@/components/ProductAccordion";
import FrequentlyBoughtTogether from "@/components/FrequentlyBoughtTogether";
import CustomersAlsoBought from "@/components/CustomersAlsoBought";
import { useColorSwatches, swatchStyle } from "@/lib/colorSwatches";

// Category-based fallback tips when a product has no admin-entered styling_tip
const DEFAULT_TIPS = {
  earrings:  "Pair these earrings with pastel outfits for a soft, elegant look.",
  necklaces: "Layer with a plain V-neck to let the pendant do the talking.",
  bracelets: "Stack with a slim watch for effortless everyday polish.",
  rings:     "A single statement ring on the index finger reads modern and confident.",
};


/** Plain product image — clicking anywhere opens the fullscreen lightbox.
 * Renders ALL provided images stacked on top of each other and toggles
 * visibility via CSS. This keeps every variant in the DOM after the first
 * paint, so switching colors is instant (zero network re-fetch) even when
 * the CDN sets no-store on the image endpoint.
 *
 * Also supports left/right swipe on touch devices to move to the next / prev
 * photo within the current color's filtered list. */
function ProductImage({ srcs, blurSrc, activeIndex, alt, onOpen, onSwipe }) {
  const startX = useRef(null);
  const startY = useRef(null);
  const moved = useRef(false);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    moved.current = false;
  };
  const onTouchMove = (e) => {
    if (startX.current == null) return;
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) moved.current = true;
  };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? startX.current) - startX.current;
    startX.current = null;
    startY.current = null;
    if (moved.current && Math.abs(dx) > 40 && onSwipe) {
      onSwipe(dx < 0 ? +1 : -1);
    }
  };

  return (
    <button
      type="button"
      onClick={(e) => {
        // Ignore click if a swipe was in progress on touch.
        if (moved.current) { e.preventDefault(); moved.current = false; return; }
        onOpen();
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className="relative aspect-square bg-white w-full block cursor-zoom-in select-none overflow-hidden touch-pan-y"
      data-testid="pd-main-image"
      aria-label="Open full-screen view"
    >
      {blurSrc && <img src={blurSrc} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain p-4 sm:p-6 blur-md" data-testid="pd-blur-placeholder" />}
      {(srcs || []).map((src, i) => (
        <img
          key={src}
          src={src}
          alt={i === activeIndex ? alt : ""}
          className={`absolute inset-0 w-full h-full object-contain p-4 sm:p-6 transition-opacity duration-150 ${i === activeIndex ? "opacity-100" : "opacity-0"}`}
          draggable={false}
          loading={i === activeIndex ? "eager" : "lazy"}
          fetchPriority={i === activeIndex ? "high" : "auto"}
          decoding="async"
          aria-hidden={i !== activeIndex}
        />
      ))}    </button>
  );
}

/** Fullscreen lightbox — shows the ENTIRE photo (no cropping) with a row of
 *  thumbnails below to switch between other photos in the same color variant. */
/** Fullscreen lightbox — shows the ENTIRE photo (no cropping) with a row of
 *  thumbnails below to switch between other photos in the same color variant.
 *
 *  PERFORMANCE NOTES on the zoom gesture:
 *    - During a pinch / pan / wheel, we DO NOT call setState per frame. That
 *      would re-render the whole component (and re-run every child effect) at
 *      60Hz, which caused visible jank on mid-tier phones.
 *    - Instead we keep scale/tx/ty in refs, batch DOM writes with
 *      requestAnimationFrame, and mutate `imgRef.current.style.transform`
 *      directly. React only re-renders when the gesture FINISHES (to update
 *      the cursor style, transition timing, and mark scale for the parent).
 */
function FullscreenViewer({ images, placeholderImages, active, onChange, alt, onClose }) {
  // Live gesture state — refs are mutated on every pointer move without triggering re-renders.
  const scaleRef = useRef(1);
  const txRef = useRef(0);
  const tyRef = useRef(0);
  // React state only bumps when the gesture ENDS — used for cursor + snap transitions.
  const [zoomed, setZoomed] = useState(false);

  // Pointer + gesture tracking. All gesture math lives in refs so pointermove
  // never re-renders React.
  const ptrs = useRef(new Map());
  const gesture = useRef({
    mode: "idle",           // "idle" | "pinch" | "pan" | "swipe"
    startDist: 0,
    startScale: 1,
    startCx: 0,             // pinch focal relative to wrap-center at pinch-start
    startCy: 0,
    startTx: 0,
    startTy: 0,
    panStartX: 0,
    panStartY: 0,
    // velocity trail (for momentum)
    trail: [],              // [{x, y, t}]
    lastTapTime: 0,
    rafId: 0,
    momentumRaf: 0,
  });
  const imgWrapRef = useRef(null);
  const imgRef = useRef(null);

  // Push the current ref values to the DOM in the next animation frame.
  const scheduleTransformFlush = () => {
    if (gesture.current.rafId) return;
    gesture.current.rafId = requestAnimationFrame(() => {
      gesture.current.rafId = 0;
      const el = imgRef.current;
      if (!el) return;
      el.style.transform = `translate3d(${txRef.current}px, ${tyRef.current}px, 0) scale(${scaleRef.current})`;
    });
  };

  const syncZoomedState = () => {
    const isZoomed = scaleRef.current > 1.02;
    setZoomed((prev) => (prev !== isZoomed ? isZoomed : prev));
  };

  const cancelMomentum = () => {
    if (gesture.current.momentumRaf) {
      cancelAnimationFrame(gesture.current.momentumRaf);
      gesture.current.momentumRaf = 0;
    }
  };

  // Wrap-center in viewport coords (used for focal-point zoom math).
  const wrapCenter = () => {
    const el = imgWrapRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };

  // Reset zoom whenever the user navigates to a different photo.
  useEffect(() => {
    cancelMomentum();
    scaleRef.current = 1;
    txRef.current = 0;
    tyRef.current = 0;
    if (imgRef.current) {
      imgRef.current.style.transition = "transform 0.22s cubic-bezier(.22,.61,.36,1)";
      imgRef.current.style.transform = "translate3d(0,0,0) scale(1)";
    }
    setZoomed(false);
  }, [active]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onChange(Math.min(images.length - 1, active + 1));
      if (e.key === "ArrowLeft")  onChange(Math.max(0, active - 1));
      if (e.key === "0")          resetZoom();
    };
    window.addEventListener("keydown", key);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", key);
      if (gesture.current.rafId) cancelAnimationFrame(gesture.current.rafId);
      cancelMomentum();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, onChange, active, images.length]);

  // Desktop wheel-zoom around cursor position. Kept smooth by NOT snapping to
  // integer scales and by using the same focal-zoom math as pinch.
  useEffect(() => {
    const el = imgWrapRef.current;
    if (!el) return;
    const handler = (e) => {
      // Only intercept when it's a real zoom gesture (ctrl+scroll on trackpad or big deltaY)
      if (!e.ctrlKey && Math.abs(e.deltaY) < 4) return;
      e.preventDefault();
      cancelMomentum();
      // Trackpad pinch on Mac emits ctrlKey with small deltaY; use smaller step.
      const step = e.ctrlKey ? 0.0035 : 0.0018;
      const factor = Math.exp(-e.deltaY * step);
      zoomAround(scaleRef.current * factor, e.clientX, e.clientY, /*animate=*/false);
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!images || images.length === 0) return null;
  const currentSrc = images[active];

  // Clamp translation so the zoomed image can't be dragged off-screen at scale>=1.
  const clampPan = (nx, ny, s) => {
    const el = imgWrapRef.current;
    if (!el) return { x: nx, y: ny };
    const w = el.clientWidth;
    const h = el.clientHeight;
    const maxX = Math.max(0, ((s - 1) * w) / 2);
    const maxY = Math.max(0, ((s - 1) * h) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, nx)),
      y: Math.max(-maxY, Math.min(maxY, ny)),
    };
  };

  const resetZoom = () => {
    cancelMomentum();
    scaleRef.current = 1;
    txRef.current = 0;
    tyRef.current = 0;
    if (imgRef.current) {
      imgRef.current.style.transition = "transform 0.25s cubic-bezier(.22,.61,.36,1)";
      imgRef.current.style.transform = "translate3d(0,0,0) scale(1)";
    }
    syncZoomedState();
  };

  // Zoom to `targetScale` while keeping the world-point currently under
  // (focalX, focalY) fixed on screen. Used by pinch, wheel, and double-tap.
  const zoomAround = (targetScale, focalX, focalY, animate = false) => {
    const s = Math.max(0.6, Math.min(6, targetScale));
    const wc = wrapCenter();
    const cx = focalX - wc.x;
    const cy = focalY - wc.y;
    // Point on the un-transformed image at pixel offset (px, py) from image center:
    //   px = (cx - tx) / scale
    // We want the same (px, py) to remain under the (unchanged) focal point:
    //   newTx = cx - px * newScale  = cx - (cx - tx) * (newScale/scale)
    const ratio = s / scaleRef.current;
    let nx = cx - (cx - txRef.current) * ratio;
    let ny = cy - (cy - tyRef.current) * ratio;
    const clampScale = Math.max(1, s); // when < 1, translation stays clamped to scale=1 bounds
    const c = clampPan(nx, ny, clampScale);
    scaleRef.current = s;
    txRef.current = s < 1 ? nx : c.x;
    tyRef.current = s < 1 ? ny : c.y;
    if (imgRef.current) {
      imgRef.current.style.transition = animate ? "transform 0.28s cubic-bezier(.22,.61,.36,1)" : "none";
      imgRef.current.style.transform = `translate3d(${txRef.current}px, ${tyRef.current}px, 0) scale(${scaleRef.current})`;
    }
    syncZoomedState();
  };

  // Double-tap zoom toggle
  const doubleTapZoom = (x, y) => {
    if (scaleRef.current > 1.05) resetZoom();
    else zoomAround(2.5, x, y, /*animate=*/true);
  };

  // ------------------ Pointer handlers ------------------

  const onPointerDown = (e) => {
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    cancelMomentum();
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    // Kill the CSS transition while fingers are down — every frame should be
    // a raw DOM write, not an interpolated animation.
    if (imgRef.current) imgRef.current.style.transition = "none";

    if (ptrs.current.size === 2) {
      // Enter pinch mode: snapshot the two-finger geometry.
      const [a, b] = [...ptrs.current.values()];
      const wc = wrapCenter();
      gesture.current.mode = "pinch";
      gesture.current.startDist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      gesture.current.startScale = scaleRef.current;
      gesture.current.startCx = (a.x + b.x) / 2 - wc.x;
      gesture.current.startCy = (a.y + b.y) / 2 - wc.y;
      gesture.current.startTx = txRef.current;
      gesture.current.startTy = tyRef.current;
      gesture.current.trail = [];
    } else if (ptrs.current.size === 1) {
      gesture.current.mode = scaleRef.current > 1.02 ? "pan" : "swipe";
      gesture.current.panStartX = e.clientX;
      gesture.current.panStartY = e.clientY;
      gesture.current.startTx = txRef.current;
      gesture.current.startTy = tyRef.current;
      gesture.current.trail = [{ x: e.clientX, y: e.clientY, t: performance.now() }];

      // Double-tap detection (only on first-finger downs)
      const now = Date.now();
      if (now - gesture.current.lastTapTime < 300) {
        doubleTapZoom(e.clientX, e.clientY);
        gesture.current.lastTapTime = 0;
        gesture.current.mode = "idle"; // don't treat as swipe/pan afterwards
      } else {
        gesture.current.lastTapTime = now;
      }
    }
  };

  const onPointerMove = (e) => {
    if (!ptrs.current.has(e.pointerId)) return;
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // ---- PINCH: focal-point zoom + follow-finger pan ----
    if (gesture.current.mode === "pinch" && ptrs.current.size >= 2) {
      const [a, b] = [...ptrs.current.values()];
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      const rawScale = gesture.current.startScale * (dist / gesture.current.startDist);
      // Allow soft over-shoot under 1× / over 5× with rubber-banding.
      let nextScale;
      if (rawScale < 1) {
        const under = 1 - rawScale;
        nextScale = 1 - under * 0.55; // damped
        nextScale = Math.max(0.6, nextScale);
      } else if (rawScale > 5) {
        const over = rawScale - 5;
        nextScale = 5 + over * 0.35;
        nextScale = Math.min(6, nextScale);
      } else {
        nextScale = rawScale;
      }
      const wc = wrapCenter();
      const cx = (a.x + b.x) / 2 - wc.x;
      const cy = (a.y + b.y) / 2 - wc.y;
      // Keep the same image-local point under the moving pinch center:
      const ratio = nextScale / gesture.current.startScale;
      const nx = cx - (gesture.current.startCx - gesture.current.startTx) * ratio;
      const ny = cy - (gesture.current.startCy - gesture.current.startTy) * ratio;
      // Only clamp when zoomed in.
      const clampScale = Math.max(1, nextScale);
      const c = clampPan(nx, ny, clampScale);
      scaleRef.current = nextScale;
      txRef.current = nextScale < 1 ? nx : c.x;
      tyRef.current = nextScale < 1 ? ny : c.y;
      scheduleTransformFlush();
      return;
    }

    // ---- PAN (1-finger while zoomed): translate directly with velocity trail ----
    if (gesture.current.mode === "pan" && ptrs.current.size === 1) {
      const dx = e.clientX - gesture.current.panStartX;
      const dy = e.clientY - gesture.current.panStartY;
      const c = clampPan(gesture.current.startTx + dx, gesture.current.startTy + dy, scaleRef.current);
      txRef.current = c.x;
      tyRef.current = c.y;
      // Trim trail to last 100ms for velocity calc
      const now = performance.now();
      gesture.current.trail.push({ x: e.clientX, y: e.clientY, t: now });
      while (gesture.current.trail.length > 6 || (gesture.current.trail.length && now - gesture.current.trail[0].t > 120)) {
        gesture.current.trail.shift();
      }
      scheduleTransformFlush();
      return;
    }

    // ---- SWIPE (1-finger while not zoomed) — mark movement for swipe-nav ----
    if (gesture.current.mode === "swipe" && ptrs.current.size === 1) {
      const dx = e.clientX - gesture.current.panStartX;
      if (Math.abs(dx) > 12) gesture.current.trail.push({ x: e.clientX, y: e.clientY, t: performance.now() });
    }
  };

  const endPointer = (e) => {
    if (!ptrs.current.has(e.pointerId)) return;
    ptrs.current.delete(e.pointerId);

    // Pinch ended when we drop from 2 pointers → 1 or 0.
    if (gesture.current.mode === "pinch" && ptrs.current.size < 2) {
      // Spring back to 1× if user pinched below it.
      if (scaleRef.current < 1.02) {
        resetZoom();
      } else if (scaleRef.current > 5) {
        // Snap the elastic overshoot back to 5.
        zoomAround(5, gesture.current.startCx + wrapCenter().x, gesture.current.startCy + wrapCenter().y, true);
      } else {
        // Re-clamp translation in case of overshoot.
        const c = clampPan(txRef.current, tyRef.current, scaleRef.current);
        txRef.current = c.x; tyRef.current = c.y;
        if (imgRef.current) {
          imgRef.current.style.transition = "transform 0.22s cubic-bezier(.22,.61,.36,1)";
          imgRef.current.style.transform = `translate3d(${c.x}px, ${c.y}px, 0) scale(${scaleRef.current})`;
        }
        syncZoomedState();
      }
      // If a finger is still down, transition into a pan/swipe mode.
      if (ptrs.current.size === 1) {
        const [p] = [...ptrs.current.values()];
        gesture.current.mode = scaleRef.current > 1.02 ? "pan" : "swipe";
        gesture.current.panStartX = p.x;
        gesture.current.panStartY = p.y;
        gesture.current.startTx = txRef.current;
        gesture.current.startTy = tyRef.current;
        gesture.current.trail = [{ x: p.x, y: p.y, t: performance.now() }];
      } else {
        gesture.current.mode = "idle";
      }
      return;
    }

    // Pan ended → apply momentum from the recent velocity trail.
    if (gesture.current.mode === "pan" && ptrs.current.size === 0) {
      const trail = gesture.current.trail;
      let vx = 0, vy = 0;
      if (trail.length >= 2) {
        const a = trail[0];
        const b = trail[trail.length - 1];
        const dt = Math.max(1, b.t - a.t);
        vx = (b.x - a.x) / dt * 16; // px per frame at 60fps
        vy = (b.y - a.y) / dt * 16;
      }
      gesture.current.mode = "idle";
      if (Math.hypot(vx, vy) > 2) {
        const decay = 0.93;
        const stopThreshold = 0.5;
        const step = () => {
          vx *= decay; vy *= decay;
          const nx = txRef.current + vx;
          const ny = tyRef.current + vy;
          const c = clampPan(nx, ny, scaleRef.current);
          const hitEdgeX = c.x !== nx;
          const hitEdgeY = c.y !== ny;
          txRef.current = c.x;
          tyRef.current = c.y;
          if (hitEdgeX) vx = 0;
          if (hitEdgeY) vy = 0;
          scheduleTransformFlush();
          if (Math.hypot(vx, vy) > stopThreshold) {
            gesture.current.momentumRaf = requestAnimationFrame(step);
          } else {
            gesture.current.momentumRaf = 0;
          }
        };
        gesture.current.momentumRaf = requestAnimationFrame(step);
      }
      syncZoomedState();
      return;
    }

    // Swipe ended → maybe navigate.
    if (gesture.current.mode === "swipe" && ptrs.current.size === 0) {
      const trail = gesture.current.trail;
      if (trail.length >= 2) {
        const first = trail[0];
        const last = trail[trail.length - 1];
        const dx = last.x - first.x;
        const dy = last.y - first.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          const dir = dx < 0 ? +1 : -1;
          const nxt = Math.min(images.length - 1, Math.max(0, active + dir));
          if (nxt !== active) onChange(nxt);
        }
      }
      gesture.current.mode = "idle";
    }

    if (ptrs.current.size === 0) syncZoomedState();
  };

  // Desktop wheel-zoom is attached earlier via useEffect (see top of component)
  // with { passive: false } so preventDefault() doesn't emit browser warnings.

  return (
    <div
      className="fixed inset-0 z-[120] bg-sj-ink/95 flex flex-col"
      onClick={onClose}
      data-testid="pd-fullscreen-viewer"
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white z-10"
        data-testid="pd-fullscreen-close"
      >
        <X size={20} />
      </button>

      {/* Main image container. touch-action:none lets us handle every gesture
          (pinch, pan, swipe) via pointer events without the browser stealing them. */}
      <div
        ref={imgWrapRef}
        className="flex-1 flex items-center justify-center p-4 sm:p-8 overflow-hidden"
        style={{ touchAction: "none" }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onPointerLeave={endPointer}
        onDoubleClick={(e) => {
          e.stopPropagation();
          doubleTapZoom(e.clientX, e.clientY);
        }}
      >
        {/* Instant-paint placeholder: the SAME w=1200 URL the PDP just rendered,
            so it's already in the browser's in-memory cache. It fills the frame
            immediately and the hi-res (w=2000) image renders on top when ready. */}
        {placeholderImages && placeholderImages[active] && (
          <img
            src={placeholderImages[active]}
            alt=""
            className="absolute max-w-full max-h-full object-contain select-none pointer-events-none"
            aria-hidden="true"
            draggable={false}
          />
        )}
        <img
          ref={imgRef}
          src={currentSrc}
          alt={alt}
          className="max-w-full max-h-full object-contain select-none will-change-transform relative"
          draggable={false}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          style={{
            transform: `translate3d(0,0,0) scale(1)`,
            transformOrigin: "center center",
            cursor: zoomed ? "grab" : "zoom-in",
          }}
          data-testid="pd-fullscreen-image"
        />
      </div>

      {/* Thumbnail rail — other photos in the same color variant */}
      {images.length > 1 && (
        <div
          className="shrink-0 pb-6 sm:pb-8"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-center gap-2 sm:gap-3 px-4 overflow-x-auto">
            {images.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onChange(i)}
                className={`shrink-0 h-16 w-16 sm:h-20 sm:w-20 rounded-md overflow-hidden bg-white/10 border-2 transition-all ${i === active ? "border-sj-gold ring-2 ring-sj-gold/40 scale-105" : "border-transparent opacity-60 hover:opacity-100"}`}
                data-testid={`pd-fullscreen-thumb-${i}`}
                aria-label={`View photo ${i + 1}`}
              >
                <img
                  src={img}
                  alt=""
                  className="w-full h-full object-contain p-1"
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { add } = useCart();
  const [searchParams] = useSearchParams();
  const initialColor = (searchParams.get("color") || "").trim() || null;
  const swatchMap = useColorSwatches();

  // If the user navigated in from a ProductCard, the full product object is
  // available in `location.state.product`. Using it as the initial state lets
  // the PDP paint instantly — no waiting for /api/products/{id} — and we then
  // refresh in the background so any admin edits are picked up.
  const preloadedProduct = location.state?.product && location.state.product.id === id
    ? location.state.product
    : null;

  const [product, setProduct] = useState(preloadedProduct);
  const [related, setRelated] = useState([]);
  const [active, setActive]   = useState(0);
  const [qty, setQty]         = useState(1);
  const [selectedColor, setSelectedColor] = useState(initialColor);
  const [fullscreen, setFullscreen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ email: "", phone: "" });
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifySent, setNotifySent] = useState(false);

  useEffect(() => {
    setActive(0);
    setSelectedColor(initialColor);
    setFullscreen(false);
    api.get(`/products/${id}`)
      .then(({ data }) => setProduct(data))
      .catch(() => nav("/shop"));
    api.get(`/products/${id}/related`).then(({ data }) => setRelated(data)).catch(() => {});
    // Instant scroll — 'smooth' behavior takes ~300ms and feels janky right
    // after a navigation. Instant feels like the page is already ready.
    window.scrollTo({ top: 0, behavior: "instant" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nav]);

  // As soon as we have a product, warm the browser cache for the FIRST few
  // color variants at both display sizes:
  //   - w=1200 → the PDP main image and thumbnail rail (already loading)
  //   - w=2000 → the fullscreen zoom viewer (loads when user taps image)
  // Capped at 3 variants x 2 sizes = 6 parallel fetches so we don't compete
  // with the visible LCP image on slow mobile networks.
  useEffect(() => {
    if (!product?.images?.length) return;
    const t = setTimeout(() => {
      product.images.slice(0, 3).forEach((src) => {
        for (const w of [1200]) {
          try {
            const preload = new window.Image();
            preload.decoding = "async";
            preload.src = resolveImage(src, { w });
          } catch (_) { /* browser refused — non-blocking */ }
        }
      });
    }, 100);
    return () => clearTimeout(t);
  }, [product]);

  if (!product) {
    return <StarLoader full label="Loading piece" />;
  }

    const allImages = product.images?.length ? product.images : [];
  // Only consider color tags that are parallel to actual images (trim to image count).
  const colorsPerImage = allImages.map((_, i) => {
    const c = (product.image_colors || [])[i];
    return c && c.trim() ? c.trim() : null;
  });
  // Distinct colors (case-insensitive, preserve first-seen casing). Only colors
  // the admin actually entered on this product's images are surfaced here —
  // no defaults, no swatches from the visual mapping dictionary.
  const uniqueColors = [];
  const seenLower = new Set();
  colorsPerImage.forEach((c) => {
    if (!c) return;
    const key = c.toLowerCase();
    if (!seenLower.has(key)) { seenLower.add(key); uniqueColors.push(c); }
  });

  // Default the color to the first entered color so the gallery filters to that
  // color's images (per spec: only show what admin uploaded per variant).
  const effectiveColor = selectedColor || (uniqueColors[0] || null);
  const filteredIndexes = effectiveColor
    ? allImages.map((_, i) => i).filter((i) => (colorsPerImage[i] || "").toLowerCase() === effectiveColor.toLowerCase())
    : allImages.map((_, i) => i);
  const images = filteredIndexes.map((i) => allImages[i]);
  const mainImg = images[active];
  // Global index (into allImages) of the currently displayed image — used to
  // toggle visibility on the stacked ProductImage without any DOM/network swap.
  const globalActiveIndex = filteredIndexes[active] ?? 0;
  const allImagesUrls1200 = allImages.map((src) => resolveImage(src, { w: 1200 }));
  const isOOS = !!product.out_of_stock;

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!notifyForm.email && !notifyForm.phone) {
      toast.error("Please add an email or phone number.");
      return;
    }
    setNotifyBusy(true);
    try {
      await api.post(`/products/${product.id}/notify`, {
        email: notifyForm.email || null,
        phone: notifyForm.phone || null,
      });
      setNotifySent(true);
      toast.success("We'll notify you when it's back!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setNotifyBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-[1440px] px-5 sm:px-10 py-10 sm:py-16" data-testid="product-page">
      <SEO
        title={`${product.name} — ${product.category?.charAt(0).toUpperCase() + product.category?.slice(1)} · Star Jewels`}
        description={(product.description || `Shop the ${product.name} — hand-crafted ${product.category} from Star Jewels. Premium artificial & fashion jewellery. Free shipping over ₹1,499.`).slice(0, 200)}
        type="product"
        image={product.images?.[0] ? resolveImage(product.images[0], { w: 1200 }) : undefined}
        path={`/product/${product.id}`}
        jsonLd={{
          "@context": "https://schema.org/",
          "@type": "Product",
          name: product.name,
          description: product.description || `${product.name} — ${product.category} from Star Jewels`,
          image: (product.images || []).slice(0, 4).map((i) => resolveImage(i, { w: 1200 })),
          category: product.category,
          brand: { "@type": "Brand", name: "Star Jewels" },
          sku: product.id,
          offers: {
            "@type": "Offer",
            price: product.price,
            priceCurrency: "INR",
            availability: product.out_of_stock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            url: `https://starjewels-store.emergent.host/product/${product.id}`,
          },
        }}
      />
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-sj-muted hover:text-sj-goldDark mb-8" data-testid="pd-back">
        <ChevronLeft size={16} /> Back to shop
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="lg:col-span-7">
          {isOOS ? (
            <div className="relative aspect-square overflow-hidden bg-white" data-testid="pd-main-image">
              {mainImg && (
                <img src={resolveImage(mainImg, { w: 1200 })} alt={product.name} className="w-full h-full object-contain p-4 sm:p-6 opacity-70 blur-[3px]" loading="eager" decoding="async" />
              )}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="pd-oos-overlay">
                <div className="bg-white/90 backdrop-blur-md px-6 py-3 rounded-full shadow-lg">
                  <div className="overline text-sj-goldDark">Currently unavailable</div>
                  <div className="font-display text-lg mt-0.5">Out of stock</div>
                </div>
              </div>
            </div>
          ) : (
            <ProductImage
              srcs={allImagesUrls1200}
              blurSrc={allImages[globalActiveIndex] && !/^https?:/.test(allImages[globalActiveIndex]) ? resolveImage(allImages[globalActiveIndex], { blur: true }) : null}
              activeIndex={globalActiveIndex}
              alt={`${product.name} — ${product.category} — Star Jewels artificial jewelry`}
              onOpen={() => setFullscreen(true)}
              onSwipe={(dir) => {
                if (images.length < 2) return;
                setActive((v) => (v + dir + images.length) % images.length);
              }}
            />
          )}

          {images.length > 1 && (
            <div className="mt-4 flex flex-wrap gap-3" data-testid="pd-thumbs">
              {images.map((img, i) => (
                // Hide the currently-active thumbnail — it duplicates the main gallery image.
                active === i ? null : (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActive(i)}
                    className="aspect-square overflow-hidden bg-white opacity-80 hover:opacity-100 transition-opacity w-[calc(20%-0.6rem)] sm:w-[calc((100%-3rem)/5)]"
                    data-testid={`pd-thumb-${i}`}
                  >
                    <img src={resolveImage(img, { w: 200 })} alt="" className="w-full h-full object-contain p-1" loading="lazy" decoding="async" width="200" height="200" />
                  </button>
                )
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-2">
              <div className="overline text-sj-goldDark">{product.category}</div>
              {product.low_stock && !isOOS && (
                <span
                  className="text-[10px] tracking-[0.14em] uppercase font-semibold bg-sj-ink text-sj-gold px-2 py-0.5 rounded-full"
                  data-testid="pd-lowstock-badge"
                >
                  Few pieces left
                </span>
              )}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-light mt-2 leading-tight" data-testid="pd-title">
              {product.name}
            </h1>
            {(() => {
              const hasMrp =
                product.original_price != null &&
                Number(product.original_price) > Number(product.price);
              const pct = hasMrp
                ? Math.round(((Number(product.original_price) - Number(product.price)) / Number(product.original_price)) * 100)
                : 0;
              return (
                <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                  <div className="font-display text-2xl font-medium" data-testid="pd-price">
                    ₹{Number(product.price).toLocaleString("en-IN")}
                  </div>
                  {hasMrp && (
                    <>
                      <div className="text-sm text-sj-muted line-through" data-testid="pd-mrp">
                        ₹{Number(product.original_price).toLocaleString("en-IN")}
                      </div>
                      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-sj-rose" data-testid="pd-discount">
                        {pct}% off
                      </div>
                    </>
                  )}
                  <div className="text-xs text-sj-muted">MRP inclusive of all taxes</div>
                </div>
              );
            })()}

            {/* Moon's Styling Tip — small, minimal, premium */}
            {(() => {
              const tip = (product.styling_tip && product.styling_tip.trim())
                || DEFAULT_TIPS[(product.category || "").toLowerCase()]
                || null;
              if (!tip) return null;
              return (
                <div
                  className="mt-5 flex items-start gap-3 p-3.5 rounded-md border border-sj-gold/25 bg-sj-cream/60"
                  data-testid="pd-moon-tip"
                >
                  <MoonAvatar size={28} className="mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-sj-goldDark">Moon's Styling Tip</div>
                    <div className="mt-1 text-sm text-sj-inkSoft italic leading-relaxed">{tip}</div>
                  </div>
                </div>
              );
            })()}

            {/* Color swatches — only shown for colors admin actually entered */}
            {uniqueColors.length > 0 && (
              <div className="mt-6" data-testid="pd-color-picker">
                <div className="text-xs text-sj-inkSoft mb-2">
                  Color: <span className="text-sj-ink font-medium">{effectiveColor}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {uniqueColors.map((c) => (
                    <button
                      key={c}
                      onClick={() => { setSelectedColor(c); setActive(0); }}
                      className={`h-9 pl-1.5 pr-3 rounded-full text-xs border inline-flex items-center gap-2 transition-colors ${effectiveColor?.toLowerCase() === c.toLowerCase() ? "border-sj-gold ring-2 ring-sj-gold/40" : "border-sj-ink/15 hover:border-sj-goldDark"}`}
                      data-testid={`pd-color-${c.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span
                        className="h-6 w-6 rounded-full border border-sj-ink/10"
                        style={swatchStyle(c, swatchMap)}
                        aria-hidden="true"
                      />
                      <span>{c}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Qty selector + Add — or Notify when OOS */}
            {!isOOS ? (
              <>
                <div className="mt-8 flex items-center gap-4">
                  <div className="inline-flex items-center rounded-full border border-sj-ink/20" data-testid="pd-qty">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-2 text-sj-inkSoft hover:text-sj-goldDark" data-testid="pd-qty-minus">−</button>
                    <span className="px-3 text-sm min-w-[24px] text-center" data-testid="pd-qty-value">{qty}</span>
                    <button onClick={() => setQty((q) => Math.min(product.stock || 99, q + 1))} className="px-4 py-2 text-sj-inkSoft hover:text-sj-goldDark" data-testid="pd-qty-plus">+</button>
                  </div>
                  <button
                    onClick={() => add(product, qty)}
                    className="btn-primary flex-1 justify-center"
                    data-testid="pd-add-to-cart"
                  >
                    <ShoppingBag size={16} /> Add to bag
                  </button>
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      add(product, qty);
                      nav("/checkout");
                    }}
                    className="btn-gold flex-1 justify-center"
                    data-testid="pd-buy-now"
                  >
                    <ShoppingBag size={16} /> Buy Now
                  </button>
                  <button type="button" className="p-3 rounded-full border border-sj-ink/15 hover:border-sj-goldDark hover:text-sj-goldDark transition-colors" aria-label="Save" data-testid="pd-save">
                    <Heart size={16} />
                  </button>
                </div>

                {/* 5-point quality check trust line */}
                <div className="mt-4 p-3 rounded-md bg-sj-cream/60 border border-sj-gold/20 flex items-start gap-2.5 text-xs text-sj-inkSoft" data-testid="pd-quality-check">
                  <ShieldCheck size={14} className="text-sj-goldDark mt-0.5 shrink-0" />
                  <span className="italic leading-relaxed">Every Star Jewels piece passes a 5-point quality check — stone setting, plating & finish, clasp strength, polish, and final packaging — before it reaches you.</span>
                </div>
              </>
            ) : (
              <div className="mt-8 space-y-4" data-testid="pd-oos-block">
                <div className="p-4 rounded-md bg-sj-cream text-sm text-sj-inkSoft">
                  This piece is currently out of stock. Leave your email or phone below and we'll ping you the moment it's back.
                </div>
                {!notifyOpen && !notifySent && (
                  <button onClick={() => setNotifyOpen(true)} className="btn-gold w-full justify-center" data-testid="pd-notify-open">
                    <BellRing size={16} /> Notify me when available
                  </button>
                )}
                {notifyOpen && !notifySent && (
                  <form onSubmit={submitNotify} className="space-y-3 p-4 rounded-md border border-sj-ink/10" data-testid="pd-notify-form">
                    <div>
                      <label className="text-xs text-sj-inkSoft" htmlFor="notify-email">Email</label>
                      <input id="notify-email" type="email" value={notifyForm.email} onChange={(e) => setNotifyForm({ ...notifyForm, email: e.target.value })}
                        placeholder="you@email.com" className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="pd-notify-email" />
                    </div>
                    <div>
                      <label className="text-xs text-sj-inkSoft" htmlFor="notify-phone">or Phone</label>
                      <input id="notify-phone" type="tel" value={notifyForm.phone} onChange={(e) => setNotifyForm({ ...notifyForm, phone: e.target.value })}
                        placeholder="+91 …" className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="pd-notify-phone" />
                    </div>
                    <button type="submit" disabled={notifyBusy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="pd-notify-submit">
                      {notifyBusy ? "Saving…" : "Notify me"}
                    </button>
                  </form>
                )}
                {notifySent && (
                  <div className="p-4 rounded-md bg-green-50 text-green-700 text-sm text-center" data-testid="pd-notify-sent">
                    ✓ You're on the list. We'll be in touch soon.
                  </div>
                )}
              </div>
            )}

            <div className="mt-10 grid grid-cols-3 gap-4 pt-6 border-t border-sj-ink/10">
              <div className="text-center">
                <Truck size={18} className="mx-auto text-sj-goldDark" />
                <div className="mt-2 text-xs">Free ship ₹1499+</div>
              </div>
              <div className="text-center">
                <Shield size={18} className="mx-auto text-sj-goldDark" />
                <div className="mt-2 text-xs">Secure payment</div>
              </div>
              <div className="text-center">
                <Sparkles size={18} className="mx-auto text-sj-goldDark" />
                <div className="mt-2 text-xs">Artisan crafted</div>
              </div>
            </div>

            {/* Collapsible sections — one click to expand, one click to collapse */}
            <div className="mt-8 border-t border-sj-ink/10" data-testid="pd-accordions">
              <AccordionItem title="About Product" testid="pd-acc-about">
                <p className="whitespace-pre-line">
                  {product.description ||
                    "A hand-picked, everyday-loveable piece from the Star Jewels boutique — designed to layer, stack and shine with every outfit."}
                </p>
                <ul className="mt-4 space-y-1.5 text-[13px]">
                  {product.material && <li><span className="text-sj-ink font-medium">Material:</span> {product.material}</li>}
                  <li><span className="text-sj-ink font-medium">Care:</span> Wipe with a dry soft cloth. Avoid contact with perfume, water & lotions to preserve shine.</li>
                </ul>
              </AccordionItem>

              <AccordionItem title="Available Offers" testid="pd-acc-offers">
                <ul className="space-y-2">
                  <li>• <span className="text-sj-ink font-medium">Bundle Deal:</span> Save more when you buy Moon's Pairings together — discount applied automatically at checkout.</li>
                  <li>• <span className="text-sj-ink font-medium">Free Shipping:</span> Complimentary shipping on all orders above ₹1,499.</li>
                  <li>• <span className="text-sj-ink font-medium">Festive Gift Wrap:</span> Every order arrives in signature Star Jewels packaging — free.</li>
                  <li>• <span className="text-sj-ink font-medium">Prepaid Bonus:</span> Small surprise added to prepaid orders.</li>
                </ul>
              </AccordionItem>

              <AccordionItem title="Shipping Policy" testid="pd-acc-shipping">
                <ul className="space-y-2">
                  <li>• <span className="text-sj-ink font-medium">Free shipping</span> across India on orders over ₹1,499.</li>
                  <li>• Standard delivery in <span className="text-sj-ink font-medium">10–12 business days</span>; metros usually faster.</li>
                  <li>• All parcels are shipped in tamper-evident, jewellery-safe packaging with tracking.</li>
                  <li>• Cash-safe checkout via Razorpay — UPI, cards, netbanking, wallets.</li>
                </ul>
              </AccordionItem>

              <AccordionItem title="Returns Policy" testid="pd-acc-returns">
                <p>
                  We do not accept general returns on jewellery for hygiene and quality reasons. However, we happily accept <span className="text-sj-ink font-medium">exchanges for damaged, defective or wrongly-shipped items</span> — just reach out within <span className="text-sj-ink font-medium">48 hours of delivery</span> with a photo of the piece.
                </p>
                <p className="mt-3">
                  <Link to="/return-policy" className="text-sj-goldDark underline underline-offset-4" data-testid="pd-acc-returns-full-link">
                    Read the full policy →
                  </Link>
                </p>
              </AccordionItem>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Moon-powered Frequently Bought Together — spans full width below the info column */}
      <FrequentlyBoughtTogether product={product} />

      {/* Customers Also Bought — real purchase-signal-based recommendations, falls back to same-category */}
      <CustomersAlsoBought product={product} />

      {/* You may also like */}
      {related.length > 0 && (
        <section className="mt-24" data-testid="pd-related">
          <div className="mb-8">
            <div className="overline">Curated for you</div>
            <h2 className="font-display text-2xl sm:text-3xl font-light mt-2">You may also like</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {fullscreen && (
        <FullscreenViewer
          // Hi-res URLs — cache-warmed on PDP mount via the preload effect
          // in the main useEffect. Down from w=2400 → w=2000 (~30% smaller
          // payload for the same on-screen sharpness up to ~3x zoom).
          images={images.map((img) => resolveImage(img, { w: 2000 }))}
          // Placeholders: the exact same w=1200 URL the PDP thumbnail rail is
          // already showing, so it paints INSTANTLY (in-memory cache hit) while
          // the w=2000 fetch completes underneath.
          placeholderImages={images.map((img) => resolveImage(img, { w: 1200 }))}
          active={active}
          onChange={setActive}
          alt={product.name}
          onClose={() => setFullscreen(false)}
        />
      )}
    </main>
  );
}
